import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import WorkspaceLayout from '@/components/WorkspaceLayout';
import { PARTNER_NAV } from '@/lib/workspaceNav';
import OfferFields, { EMPTY_OFFER, offerToForm } from '@/components/internships/OfferFields';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Save, Send, ArrowLeft, ShieldCheck, CheckCircle2, AlertTriangle, Clock, School,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from '@/pages/coordinator/AssignMentorDialog';

const FORMAT_LABEL = { on_site: 'On-site', hybrid: 'Hybrid', remote: 'Remote' };

const Row = ({ label, value, testId }) => (value ? (
  <div data-testid={testId}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#9CA3AF]">{label}</p>
    <p className="text-sm text-[#1F2937] mt-0.5 whitespace-pre-wrap leading-relaxed">{value}</p>
  </div>
) : null);

/** Closed programme: the offer exactly as it ran, with nothing editable. */
function OfferDetails({ offer: o }) {
  return (
    <div className="space-y-5" data-testid="offer-details-readonly">
      <div className="grid sm:grid-cols-2 gap-4">
        <Row label="Internship" value={o.title} testId="detail-title" />
        <Row label="Career" value={o.career_name} testId="detail-career" />
        <Row label="Company" value={o.company_name} />
        <Row label="Industry" value={o.industry} />
        <Row label="Dates" value={`${formatDay(o.start_date)} – ${formatDay(o.end_date)}${o.duration_days ? ` · ${o.duration_days} days` : ''}`} testId="detail-dates" />
        <Row label="Daily timings" value={o.daily_start_time ? `${o.daily_start_time} – ${o.daily_end_time}` : null} />
        <Row label="Places" value={o.positions ? String(o.positions) : null} testId="detail-positions" />
        <Row label="Format" value={FORMAT_LABEL[o.format] || o.format} />
        <Row label="Eligible grades" value={(o.eligible_grades || []).join(', ')} />
        <Row label="Supervisor" value={o.supervisor_name} />
        <Row label="Contact" value={[o.contact_name, o.contact_email, o.contact_phone].filter(Boolean).join(' · ')} />
      </div>
      <Row label="What the student will do" value={o.description} testId="detail-description" />
      <Row label="Activities" value={o.student_activities} />
      <Row label="Learning outcomes" value={o.learning_outcomes} />
      <Row label="Prerequisites" value={o.prerequisites} />
      <Row label="Workplace address" value={o.workplace_address} />
      <Row label="Additional instructions" value={o.additional_instructions} />
      <p className="text-[11px] text-[#9CA3AF]" data-testid="detail-approval">
        {o.reviewed_by ? `Approved by ${o.reviewed_by}` : 'Submitted'}
        {o.reviewed_at ? ` on ${formatDay(o.reviewed_at.slice(0, 10))}` : ''}
      </p>
    </div>
  );
}

export default function PartnerOfferEditor() {
  const { offerId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !offerId;

  const [loading, setLoading] = useState(true);
  const [careers, setCareers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [offer, setOffer] = useState(null);
  const [programId, setProgramId] = useState(params.get('program') || '');
  const [form, setForm] = useState(EMPTY_OFFER);
  const [declaration, setDeclaration] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, o] = await Promise.all([api.get('/careers/options'), api.get('/partner/overview')]);
      setCareers(c.data || []);
      setOverview(o.data);
      if (offerId) {
        const found = (o.data.offers || []).find(x => x.id === offerId);
        if (!found) { toast.error('Offer not found'); navigate('/partner/programs'); return; }
        setOffer(found);
        setProgramId(found.program_id);
        setForm({
          ...offerToForm(found),
          ...(found.pending_changes
            ? Object.fromEntries(Object.entries(found.pending_changes)
              .filter(([k]) => !['proposed_at', 'proposed_by'].includes(k)))
            : {}),
        });
        setDeclaration(Boolean(found.declaration || o.data.declaration?.accepted));
      } else {
        setForm(f => ({
          ...f,
          company_name: o.data.company?.name || '',
          industry: o.data.company?.industry || '',
          website: o.data.company?.website || '',
          contact_name: o.data.company?.rep_name || user?.full_name || '',
          contact_email: o.data.company?.rep_email || user?.email || '',
          contact_phone: o.data.company?.rep_phone || '',
          workplace_address: o.data.company?.address || '',
        }));
        setDeclaration(Boolean(o.data.declaration?.accepted));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open this offer');
    }
    setLoading(false);
  }, [offerId, navigate, user]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const program = (overview?.programs || []).find(p => p.id === programId) || null;
  const alreadyAccepted = Boolean(overview?.declaration?.accepted || offer?.declaration);
  const programClosed = Boolean(program && program.status !== 'open');
  const readOnly = Boolean(offer && (['submitted', 'rejected'].includes(offer.status) || programClosed));
  const approved = offer?.status === 'approved';
  const backTo = programId ? `/partner/programs/${programId}?tab=offers` : '/partner/programs';

  const save = async (submit) => {
    if (!programId) return toast.error('Choose the internship programme');
    if (submit && !alreadyAccepted && !declaration) return toast.error('Accept the partner declaration first');
    setSaving(true);
    const payload = {
      ...form, program_id: programId, submit,
      positions: form.positions === '' ? null : Number(form.positions),
      declaration_accepted: declaration,
    };
    try {
      const r = offerId ? await api.put(`/partner/offers/${offerId}`, payload)
        : await api.post('/partner/offers', payload);
      if (approved) {
        toast.success(r.data.review_state === 'pending_reapproval'
          ? 'Changes sent to the school for approval'
          : 'Offer updated');
      } else {
        toast.success(submit ? 'Offer sent to the school' : 'Draft saved');
      }
      navigate(programId ? `/partner/programs/${programId}?tab=offers` : '/partner/programs');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save this offer'); }
    setSaving(false);
  };

  if (loading) {
    return (
      <WorkspaceLayout nav={PARTNER_NAV} activeRole="partner" testId="partner-offer-editor">
        <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout nav={PARTNER_NAV} activeRole="partner" testId="partner-offer-editor">
      <div className="max-w-3xl">
        <Link to={backTo} className="text-sm text-[#6B7280] hover:text-[#1F2937] inline-flex items-center gap-1 mb-4" data-testid="back-to-offers">
          <ArrowLeft className="h-3.5 w-3.5" />Back to the programme
        </Link>
        <h1 className="text-3xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight">
          {programClosed ? 'Internship record'
            : offer?.status === 'awaiting_company' ? 'Review & confirm this internship'
              : approved ? 'Approved internship' : isNew ? 'Create an internship offer' : 'Your internship offer'}
        </h1>
        <p className="text-sm text-[#6B7280] mt-1.5 leading-relaxed">
          {programClosed
            ? 'This programme has closed. The internship is kept here exactly as it ran, and can no longer be changed.'
            : offer?.status === 'awaiting_company'
            ? `${offer.referred_by_parent_name || 'A parent'} proposed this internship on behalf of your organisation. Check every detail, correct anything that is wrong, then confirm it for the school.`
            : approved
              ? 'Families can see this internship. Changing the dates, places or grades sends the update back to the school for approval.'
              : 'Short, supervised placements of up to seven days for students in grades 9–12. You can save a draft and finish later.'}
        </p>

        {program && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mt-5" data-testid="editor-program">
            <div className="flex items-center gap-2">
              <School className="h-3.5 w-3.5 text-[#F97D6A]" />
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">{program.school_name}</p>
            </div>
            <p className="text-sm font-semibold text-[#1F2937] mt-1">{program.name}</p>
            <p className="text-[11px] text-[#4B5563] mt-1 flex flex-wrap gap-x-4">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-[#9CA3AF]" />{formatDay(program.start_date)} – {formatDay(program.end_date)}</span>
              <span>Offers by {formatDay(program.application_deadline)}</span>
              <span>Grades {program.eligible_grades?.join(', ')}</span>
            </p>
          </div>
        )}

        {isNew && !programId && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mt-4" data-testid="editor-program-picker">
            <p className="text-sm font-medium text-[#1F2937] mb-2">Choose a programme you have been invited to</p>
            <div className="space-y-2">
              {(overview?.programs || []).filter(p => p.accepting_offers).map(p => (
                <button key={p.id} onClick={() => setProgramId(p.id)} className="w-full text-left px-3 py-2.5 rounded-xl border border-gray-200 hover:border-[#F97D6A] transition-colors" data-testid={`pick-program-${p.id}`}>
                  <p className="text-sm font-medium text-[#1F2937]">{p.name}</p>
                  <p className="text-[11px] text-[#6B7280]">{p.school_name} · offers by {formatDay(p.application_deadline)}</p>
                </button>
              ))}
              {!(overview?.programs || []).some(p => p.accepting_offers) && (
                <p className="text-xs text-[#9CA3AF]">No programme is open for offers right now.</p>
              )}
            </div>
          </div>
        )}

        {offer?.coordinator_note && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mt-4 leading-relaxed" data-testid="editor-school-note">
            School note: “{offer.coordinator_note}”
          </p>
        )}
        {offer?.review_state === 'pending_reapproval' && (
          <p className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-xl px-4 py-2.5 mt-4 leading-relaxed" data-testid="editor-pending-changes">
            Your proposed changes are with the school. Until they are approved, families still see the previously
            approved details.
          </p>
        )}

        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 space-y-5 mt-4">
          {programClosed && offer ? (
            <OfferDetails offer={offer} />
          ) : (
            <fieldset disabled={readOnly} className={readOnly ? 'opacity-95' : ''} data-testid={readOnly ? 'offer-fields-locked' : 'offer-fields'}>
              <OfferFields form={form} set={set} careers={careers} program={program} lockCompanyName={!isNew}
                careerStatus={offer?.career_mapping_status} />
            </fieldset>
          )}

          {!alreadyAccepted && !programClosed && (
            <label className="flex items-start gap-2.5 bg-[#FEF0EC] rounded-xl px-4 py-3 cursor-pointer" data-testid="declaration-box">
              <input type="checkbox" checked={declaration} onChange={e => setDeclaration(e.target.checked)} className="mt-0.5 accent-[#F97D6A]" data-testid="declaration-checkbox" />
              <span className="text-sm text-[#4B5563] leading-relaxed">
                <strong className="text-[#1F2937] block mb-1">Partner declaration</strong>
                {(overview?.declaration?.points || []).map(p => <span key={p} className="block">• {p}</span>)}
              </span>
            </label>
          )}
          {alreadyAccepted && (
            <p className="text-[11px] text-emerald-600 flex items-center gap-1.5" data-testid="declaration-on-file">
              <CheckCircle2 className="h-3.5 w-3.5" />Partner declaration accepted
              {overview?.declaration?.accepted?.accepted_at ? ` on ${formatDay(overview.declaration.accepted.accepted_at.slice(0, 10))}` : ''}.
            </p>
          )}

          {readOnly ? (
            <div className="pt-1">
              <Badge className="border-0 bg-sky-50 text-sky-700 text-[10px]" data-testid="editor-readonly">
                {programClosed ? 'Programme closed — read-only'
                  : offer.status === 'submitted' ? 'With the school for approval' : 'Not accepted this round'}
              </Badge>
              <p className="text-[11px] text-[#9CA3AF] mt-2">
                {programClosed
                  ? 'Nothing on a closed programme can be edited or resubmitted.'
                  : offer.status === 'submitted'
                    ? 'You can edit again if the school asks for changes.'
                    : 'The school has closed this offer.'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              {!approved && (
                <Button variant="outline" disabled={saving} onClick={() => save(false)} className="rounded-xl h-11 font-semibold border-gray-200" data-testid="save-draft-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save draft
                </Button>
              )}
              <Button disabled={saving} onClick={() => save(!approved)} className="rounded-xl h-11 font-semibold bg-[#F97D6A] text-white hover:bg-[#F06651] flex-1" data-testid="submit-offer-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {approved ? 'Send changes to the school'
                  : offer?.status === 'awaiting_company' ? 'Confirm & send to the school' : 'Submit to the school'}
              </Button>
            </div>
          )}

          {approved && !programClosed && (
            <p className="text-[11px] text-[#9CA3AF] flex items-start gap-1.5 leading-relaxed">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
              Changes to dates, places, grades or supervision need the school to approve them again. Wording tidy-ups
              go live straight away.
            </p>
          )}
          {offer?.revision_count > 0 && (
            <p className="text-[11px] text-[#9CA3AF]" data-testid="editor-history">
              {offer.revision_count} change{offer.revision_count === 1 ? '' : 's'} recorded on this offer.
            </p>
          )}
          <p className="text-[11px] text-[#9CA3AF] flex items-start gap-1.5 leading-relaxed">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-px" />
            Student applications and resumes stay with the school. You will be asked to confirm students in a later step.
          </p>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
